import React, { useEffect, useRef, useState } from 'react'
import { LogOut, ShieldCheck, User } from 'lucide-react'
import { useAuth, useLogout } from '../hooks/useAuth'

// Header affordance for the signed-in account: who am I, and the way out.
// Renders nothing when auth is off (VERALIS_AUTH=off) — the dev experience
// should look exactly as it did before the layer existed.
export default function UserMenu() {
  const { enabled, user, isAdmin } = useAuth()
  const logout = useLogout()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false) }
    const onKey = (e) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!enabled || !user) return null
  const name = user.display_name || user.username
  const initials = String(name).trim().slice(0, 2).toUpperCase()

  return (
    <div className="relative ml-1" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Signed in as ${name}`}
        className="flex items-center justify-center w-8 h-8 rounded-full border border-gold/50 bg-white/70 text-[11px] font-semibold text-gold-deep hover:border-gold dark:border-gold/30 dark:bg-midnight-800 dark:text-gold"
      >
        {initials}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 rounded-lg border border-parchment-border bg-parchment-50 shadow-xl overflow-hidden dark:border-midnight-600 dark:bg-midnight-800 z-50"
        >
          <div className="px-4 py-3 border-b border-parchment-border dark:border-midnight-600">
            <div className="flex items-center gap-2 text-sm text-ink dark:text-mist">
              <User className="w-3.5 h-3.5 shrink-0 text-ink-soft dark:text-mist/50" />
              <span className="truncate" title={name}>{name}</span>
            </div>
            <div className="mt-1 flex items-center gap-1.5 text-[11px] text-ink-soft dark:text-mist/50">
              {isAdmin && <ShieldCheck className="w-3 h-3 text-gold-deep dark:text-gold" />}
              {isAdmin ? 'Administrator' : 'User'} · {user.username}
            </div>
          </div>
          <button
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-ink-soft hover:bg-parchment-200 hover:text-ink disabled:opacity-60 dark:text-mist/70 dark:hover:bg-midnight-700 dark:hover:text-mist"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      )}
    </div>
  )
}
